import { QueryClientProvider } from "@tanstack/react-query";
import { RouterProvider } from "@tanstack/react-router";
import { LockKeyholeIcon } from "lucide-react";
import * as React from "react";

import { ThemeProvider } from "@/components/theme-provider";
import { AuthProvider, useAuth } from "@/features/auth/auth-provider";
import { queryClient } from "@/lib/query-client";
import { router } from "@/router";

export function App() {
  return (
    <ThemeProvider>
      <QueryClientProvider client={queryClient}>
        <AuthProvider>
          <RouterGate />
        </AuthProvider>
      </QueryClientProvider>
    </ThemeProvider>
  );
}

/**
 * Oturum çözülene kadar yönlendiriciyi kurmaz.
 *
 * Rota korumaları `beforeLoad` içinde çalışıyor ve orada "henüz bilmiyorum"
 * diye bir durum yok — bekletmeseydik sayfa her yenilendiğinde bir an giriş
 * ekranı görünür, ardından asıl sayfaya atlardı.
 */
function RouterGate() {
  const auth = useAuth();

  // `routerMounted`, <RouterProvider>'ın gerçek `auth` bağlamıyla ilk kez
  // mount edildiği ana kadar `false` kalır.
  //
  // Bu olmadan: `auth.status` "loading"dan çıktığı ilk anda bu effect de
  // tetikleniyor ve henüz hiçbir <Transitioner> abone olmadığı için
  // `router.invalidate()` router'ı HEMEN, `<RouterProvider>` henüz mount
  // OLMADAN ve context'i (`router.update()` ile) güncellemeden yüklemeye
  // zorluyordu. Bu da router.tsx'teki YER TUTUCU bağlamla (`auth: undefined!`)
  // bir `beforeLoad` çalıştırıp "context.auth is undefined" hatası
  // fırlatıyordu — çıkış yapmış bir kullanıcı sayfayı yenilediğinde bir anlık
  // "Bir şeyler ters gitti" ekranı, ardından /giris'e yönlenme olarak
  // görünüyordu. İlk çözümlemeyi atlamak güvenli: <RouterProvider> zaten o an
  // doğru bağlamla ilk kez mount oluyor, ayrıca invalidate edilmesine gerek
  // yok. Sonraki durum değişiklikleri (giriş/çıkış, başka sekmede oturum
  // bitmesi) her zamanki gibi invalidate ediliyor.
  const routerMounted = React.useRef(false);

  React.useEffect(() => {
    if (!routerMounted.current) return;
    void router.invalidate();
  }, [auth.status]);

  if (auth.status === "loading") return <SplashScreen />;

  routerMounted.current = true;

  return <RouterProvider router={router} context={{ auth }} />;
}

function SplashScreen() {
  return (
    <div className="bg-background flex min-h-dvh flex-col items-center justify-center gap-4">
      <div className="bg-primary text-primary-foreground flex size-12 animate-pulse items-center justify-center rounded-xl">
        <LockKeyholeIcon className="size-6" />
      </div>
      <p className="text-muted-foreground text-sm">EBridge Lisans yükleniyor…</p>
    </div>
  );
}
